/**
 * MpResourceLoadChart.js — MP 비교 탭 경고 표에서 여는 "일자별 부하 추이" 차트.
 *
 * 공용 표(CompareFeasibilityWarnings)는 이 기능을 모른다. 표는 renderCell 훅으로
 * 셀 렌더를 모듈에 위임할 뿐이고, 버튼 모양도 차트도 전부 여기에 있다.
 *   MpChartButton      — action 열에 놓는 진입 버튼
 *   MpResourceLoadChart — 일자별 추이 모달
 */
import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "@plannel/utils/i18n";
import numberUtil from "@plannel/utils/number-util";
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { blue, green, red, purple } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend, ReferenceLine,
} from "recharts";
import { AgTooltipBox, AgTooltipRow, AG_TOOLTIP_HEADING_STYLE } from "@plannel/components/rechart/AgStyleTooltip";
import { gridLine, axisTick } from "@plannel/components/dashboard/chartTokens";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

// 진입 버튼 — 공용 표의 action 열에 renderCell 로 꽂는다.
export function MpChartButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("menuMpResourceUtilization")}>
      <IconButton size="small" onClick={onClick}
        sx={{ justifySelf: "center", p: "2px", color: C.text3, "&:hover": { color: C.acc } }}>
        <ShowChartIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );
}

export function MpResourceLoadChart({ chartRes, setChartRes }) {
  const { t: tr } = useTranslation();
  // 그리드·축 눈금은 앱 공용 차트 토큰을 쓴다 — 로컬 C.* 하드코딩을 두면 테마와 어긋난다.
  const theme = useTheme();
  const tick = axisTick(theme);
  // toLocaleString(undefined) 는 브라우저 로케일을 타서 사용자 PC 마다 구분자가 달라진다(de-DE: 1.234,5).
  //   앱은 numberUtil 로 로케일을 "en" 에 고정해 전 화면 표기를 통일한다.
  const fmtH = (v) => `${numberUtil.formatNumber(v || 0, { maximumFractionDigits: 1 })} ${i18n.t("ProblemDateHour")}`;
// 차트 색상 — 표준 `reports/ResourceUtilization.js` 에 맞춘다(이 차트의 드릴다운 대상 화면).
//   util 의 #FFD56F 는 표준이 ABCClassConfig.AZ 배경색을 재사용한 값이다. 의미상 연결이 아니라
//   우연한 결합이므로 그 참조까지 따라가지 않고 값만 맞춘다.
//   nonwork 는 표준에 대응 계열이 없다 — MUI 팔레트 밝기에 맞춰 red 와 hue 를 분리(해칭 병행).
const CH = { load: blue[400], base: green[300], over: red[300], util: "#FFD56F", nonwork: purple[300] };
// series → 단일 누적 막대. within=기준 능력 이내 부하 / remain=여유 / over_h=과잉.
//   비가동(kind="nonworking")은 base_h=0 이라 '초과'가 아니므로 nonwork(보라 해칭)로 분리한다.
const toChartData = (series, kind) => (Array.isArray(series) ? series : []).map((s) => {
  const nw = kind === "nonworking" && (s.base_h || 0) === 0;
  return {
    ...s,
    within: nw ? 0 : Math.max(0, (s.load_h || 0) - (s.over_h || 0)),
    remain: nw ? 0 : Math.max(0, (s.base_h || 0) - (s.load_h || 0)),
    over_h: nw ? 0 : (s.over_h || 0),
    nonwork: nw ? (s.load_h || 0) : 0,
    util: s.base_h > 0 ? Math.round((s.load_h / s.base_h) * 1000) / 10 : null,
  };
});

const ResTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  // 비가동일(능력 0h 인데 배정): '과잉 부하'가 아니라 '비가동일 배정'(보라)으로 표기 — 능력초과와 개념 구분.
  const isNwDay = chartRes?.kind === "nonworking" && (d.base_h || 0) === 0;
  // 생김새는 앱 공용(ag-charts 통일) 부품에 위임 — 내용만 이 차트 고유다.
  return (
    <AgTooltipBox>
      <div style={AG_TOOLTIP_HEADING_STYLE}>{d.date}</div>
      <AgTooltipRow color={CH.base} label={tr("mpBaseCapaLabel")} value={fmtH(d.base_h)} />
      <AgTooltipRow color={CH.load} label={tr("capaLoad")} value={fmtH(d.load_h)} />
      {isNwDay ? (
        <AgTooltipRow color={CH.nonwork} label={tr("mpNonworkingAssignLabel")} value={fmtH(d.load_h)} />
      ) : (
        <AgTooltipRow color={CH.over} label={tr("capaOver")} value={fmtH(d.over_h)} />
      )}
      <AgTooltipRow color={CH.util} label={tr("mpUtilLabel")} value={d.util != null ? `${d.util}%` : "—"} />
    </AgTooltipBox>
    );
  };

  // 일자별 추이 — 모달(가로로 넓게). 막대: 기준 능력/부하/과잉 부하 · 선: 가동률
  return (
    <Dialog open={!!chartRes} onClose={() => setChartRes(null)} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}>
      {/* 헤더 — 아이콘 배지 + 제목 + 통계 pill. 비가동(kind="nonworking")은 빨강(g3) 톤·EventBusy 아이콘(카드와 정합). */}
      <DialogTitle component="div" sx={{ p: "14px 18px",
        bgcolor: chartRes?.kind === "nonworking" ? C.g3l : C.g2l,
        borderBottom: `1px solid ${chartRes?.kind === "nonworking" ? C.g3s : C.g2s}` }}>
        <Stack direction="row" alignItems="center" gap={1.2}>
          <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#fff",
            border: `1px solid ${chartRes?.kind === "nonworking" ? C.g3s : C.g2s}`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {chartRes?.kind === "nonworking"
              ? <EventBusyIcon sx={{ fontSize: 19, color: C.g3t }} />
              : <WarningAmberIcon sx={{ fontSize: 19, color: C.g2t }} />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: FONT.H2, fontWeight: 800, color: C.text1, lineHeight: 1.3 }}>
              {chartRes?.res_name || chartRes?.res_cd}
              {chartRes?.res_cd && chartRes?.res_name && (
                <Typography component="span" sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text3, ml: 0.8 }}>
                  {chartRes.res_cd}
                </Typography>
              )}
            </Typography>
            <Typography noWrap sx={{ fontSize: FONT.MICRO, color: C.text2 }}>{chartRes?.loc_name || chartRes?.loc_cd}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setChartRes(null)} sx={{ flexShrink: 0, color: C.text3 }}>
            <CloseIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 1 }}>
          {[
            ...(chartRes?.res_cnt != null ? [{ label: tr("resCnt"), value: `${numberUtil.formatNumber(chartRes.res_cnt, { maximumFractionDigits: 0 })}${tr("mpUnitCountSuffix")}`, color: C.text1 }] : []),
            { label: chartRes?.kind === "nonworking" ? tr("mpCumAssignLabel") : tr("mpCumOverloadHoursLabel"), value: fmtH(chartRes?.over_hours), color: C.g3t },
            { label: chartRes?.kind === "nonworking" ? tr("mpAssignDaysLabel") : tr("mpOverloadDaysLabel"), value: tr("mpDayCountLabel", { count: chartRes?.over_days || 0 }), color: C.text1 },
            { label: tr("mpPeakLoadDateLabel"), value: `${chartRes?.peak_date || "—"} (${fmtH(chartRes?.peak_hours)})`, color: C.text1 },
          ].map((p) => (
            <Box key={p.label} sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5,
              px: "9px", py: "3px", borderRadius: "7px", bgcolor: "#fff", border: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, fontWeight: 600 }}>{p.label}</Typography>
              <Typography sx={{ fontSize: FONT.CAPTION, color: p.color, fontWeight: 800 }}>{p.value}</Typography>
            </Box>
          ))}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: "16px 18px 18px" }}>
        {(() => {
          const nwKind = chartRes?.kind === "nonworking";
          const data = toChartData(chartRes?.series, chartRes?.kind);
          // 가동률 축 — 하한 100% 고정. "auto" 는 축 상단을 데이터 최댓값에서 역산해 저부하 자원이 포화로 보인다.
          //   눈금을 직접 주어 100 을 항상 축에 남긴다. 임계값 표기는 플롯 안이 아니라 축이 맡아야 막대와 안 헷갈린다.
          const utilMax = data.reduce((mx, d) => Math.max(mx, d.util || 0), 0);
          const utilTop = Math.max(100, Math.ceil(utilMax / 50) * 50);
          const utilStep = utilTop <= 100 ? 25 : 50;
          const utilTicks = Array.from({ length: utilTop / utilStep + 1 }, (_, i) => i * utilStep);
          // 임계선은 100% 초과일 때만 — 이하면 축 상단이 곧 100% 라 선이 플롯 테두리와 겹친다.
          const overCapa = utilMax > 100;
          // 막대 표시는 kind 가 아니라 실제 데이터로 판단한다 — 비가동 차트에도 근무일이 섞여 있어
          //   kind 로 끄면 그 날의 초과·여유가 통째로 사라진다(휴무일은 toChartData 가 이미 0 으로 만든다).
          const hasOver = data.some((d) => (d.over_h || 0) > 0);
          const hasRemain = data.some((d) => (d.remain || 0) > 0);
          if (data.length === 0) {
            return <Typography sx={{ fontSize: FONT.BODY, color: C.text3, py: 6, textAlign: "center" }}>{tr("mpNoDateDataDesc")}</Typography>;
          }
          return (
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={data} barCategoryGap="8%" margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridLine(theme)} vertical={false} />
                <XAxis dataKey="date" tick={tick} minTickGap={28} axisLine={{ stroke: C.border }} tickLine={false} />
                <YAxis yAxisId="left" tick={tick} axisLine={false} tickLine={false}
                  label={{ value: tr("mpHoursUnitAxisLabel"), angle: -90, position: "insideLeft", style: { fontSize: 10, fill: C.text3, textAnchor: "middle" } }} />
                <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, utilTop]} ticks={utilTicks} tick={tick} axisLine={false} tickLine={false}
                  label={{ value: tr("mpUtilLabel"), angle: 90, position: "insideRight", style: { fontSize: 10, fill: C.text3, textAnchor: "middle" } }} />
                <RechartsTooltip content={<ResTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                {/* iconType 을 지정하지 않는다 — 표준 차트처럼 시리즈 종류가 모양을 정하게 둔다(막대=사각, 선=선). */}
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                {/* 능력초과 차트: 부하(파랑)+능력 여유(초록)+과잉(빨강). 비가동 차트: 부하(파랑)+비가동일 배정(보라 해칭).
                    비가동일(base=0)은 여유·과잉이 무의미하므로 toChartData 가 그 날만 0 으로 만들고 nonwork 로 옮긴다.
                    과잉은 kind 가 아닌 hasOver 로 켠다 — 비가동 차트의 근무일 초과분까지 숨기면 안 된다. */}
                <Bar yAxisId="left" dataKey="within" name={tr("capaLoad")}          stackId="cap" fill={CH.load} maxBarSize={48}
                  isAnimationActive animationBegin={0}   animationDuration={500} animationEasing="ease-out" />
                {hasRemain && (
                <Bar yAxisId="left" dataKey="remain" name={tr("capaAvail")} stackId="cap" fill={CH.base} maxBarSize={48}
                  isAnimationActive animationBegin={500} animationDuration={250} animationEasing="ease-out" />
                )}
                {hasOver && (
                <Bar yAxisId="left" dataKey="over_h" name={tr("capaOver")}      stackId="cap" fill={CH.over} radius={[3, 3, 0, 0]} maxBarSize={48}
                  isAnimationActive animationBegin={750} animationDuration={350} animationEasing="ease-out" />
                )}
                {nwKind && (
                <Bar yAxisId="left" dataKey="nonwork" name={tr("mpNonworkingAssignLabel")} stackId="cap" fill={CH.nonwork} radius={[3, 3, 0, 0]} maxBarSize={48}
                  isAnimationActive animationBegin={500} animationDuration={350} animationEasing="ease-out" />
                )}
                {/* 가동률 100% = 능력을 정확히 소진하는 판단 임계선. 초과가 곧 이상은 아니므로 중립 회색 파선.
                    라벨은 달지 않는다 — 우측 축에 100% 눈금이 항상 있고, 플롯 안 텍스트는 막대를 가리키는 것처럼 읽힌다. */}
                {overCapa && (
                <ReferenceLine yAxisId="right" y={100} stroke={C.text3} strokeDasharray="4 3" strokeWidth={1} />
                )}
                <Line yAxisId="right" dataKey="util" name={tr("mpUtilLabel")} stroke={CH.util} strokeWidth={2.5} dot={false} connectNulls
                  isAnimationActive animationBegin={1100} animationDuration={500} animationEasing="ease-out" />
              </ComposedChart>
            </ResponsiveContainer>
          );
        })()}
      </DialogContent>
    </Dialog>

  );
}
